/*
  blog-archive.js

  2026-10-23: the filters and the page number live in the address bar, so a
  filtered list can be linked to and the browser's Back button steps back
  through recipe pages instead of leaving the page.
    ?q=      search text (as typed)
    ?tag=    tag, exactly as on the article (e.g. Vegan)
    ?product= product, exactly as in custom.recipe_products
    ?p=      page number. NOT ?page=: Shopify's paginate reads that one and
             would serve an empty list.
  Picking a filter or a page adds a history step; typing in the search box
  only keeps the address up to date. A link that carries filters keeps the
  grid hidden (the section adds .is-restoring before this script runs) until
  the filtered cards are drawn, so the unfiltered first page never flashes.

  Also 2026-10-23: no redraw on first load. The section now prints the count
  line and the page buttons itself, and on phones hides the server's cards
  past the mobile page size with CSS, so this script no longer throws the
  first cards away and draws new ones. Cards drawn here use the same image
  sizes as snippets/blog-archive-card.liquid.
*/
if (!customElements.get('blog-archive')) {
  customElements.define(
    'blog-archive',
    class BlogArchive extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
        this.perPageDesktop = parseInt(this.dataset.perPage, 10) || 12;
        this.perPageMobile = parseInt(this.dataset.perPageMobile, 10) || 6;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.perPage = this.mobileQuery.matches ? this.perPageMobile : this.perPageDesktop;
        this.showTitle = this.dataset.showTitle === 'true';
        this.showAuthor = this.dataset.showAuthor === 'true';
        this.showDate = this.dataset.showDate === 'true';
        this.showTags = this.dataset.showTags === 'true';
        this.showRatings = this.dataset.showRatings === 'true';
        this.accentScheme = this.dataset.accentScheme || 'scheme-1';
        this.productFilterFormat = this.dataset.productFilterFormat || 'handle';
        this.tagFilterAllLabel = this.dataset.tagFilterAllLabel || 'All posts';
        this.productFilterAllLabel = this.dataset.productFilterAllLabel || 'All products';

        this.gridEl = this.querySelector('[data-blog-archive-grid]');
        this.countEl = this.querySelector('[data-blog-archive-count]');
        this.paginationEl = this.querySelector('[data-blog-archive-pagination]');
        this.emptyEl = this.querySelector('[data-blog-archive-empty]');
        this.searchInput = this.querySelector('[data-blog-archive-search]');
        this.tagSelect = this.querySelector('[data-blog-archive-tag-filter]');
        this.productSelect = this.querySelector('[data-blog-archive-product-filter]');

        this.currentPage = 1;
        this.filters = { search: '', tag: '', product: '' };
        this.searchRaw = '';
        this.allArticles = this.readEmbeddedArticles(this);
        this.filtered = [...this.allArticles];

        this.populateTagFilter();
        this.populateProductFilter();
        this.bindEvents();

        // A link with filters in it: draw the filtered list straight away.
        // Otherwise keep the server's cards (on phones, CSS already hides the
        // ones past the mobile page size) and only wire up the controls.
        if (this.readUrlState()) {
          this.applyFilters();
          // Tidy the address if it asked for a value or page that doesn't
          // exist, so a copied link shows what is actually on screen.
          this.writeUrl('replace');
        } else {
          this.renderCurrentPage({ skipGrid: true });
        }
        this.classList.remove('is-restoring');

        this.onPopState = () => {
          const before = this.currentPage;
          this.readUrlState();
          this.applyFilters();
          if (this.currentPage !== before) {
            requestAnimationFrame(() => this.scrollIntoView({ block: 'start' }));
          }
        };
        window.addEventListener('popstate', this.onPopState);

        if (this.totalPages > 1) {
          this.fetchRemainingPages();
        }

        this.onBreakpointChange = (event) => {
          const nextPerPage = event.matches ? this.perPageMobile : this.perPageDesktop;
          if (nextPerPage === this.perPage) return;
          this.perPage = nextPerPage;
          this.currentPage = 1;
          this.renderCurrentPage();
          this.writeUrl('replace');
        };
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', this.onBreakpointChange);
        } else if (this.mobileQuery.addListener) {
          this.mobileQuery.addListener(this.onBreakpointChange);
        }
      }

      disconnectedCallback() {
        if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
        if (!this.mobileQuery || !this.onBreakpointChange) return;
        if (this.mobileQuery.removeEventListener) {
          this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
        } else if (this.mobileQuery.removeListener) {
          this.mobileQuery.removeListener(this.onBreakpointChange);
        }
      }

      readEmbeddedArticles(root) {
        const script = root.querySelector('script[data-blog-archive-payload]');
        if (!script) return [];
        try {
          const data = JSON.parse(script.textContent);
          return Array.isArray(data.articles) ? data.articles : [];
        } catch (error) {
          return [];
        }
      }

      // Reads ?q= ?tag= ?product= ?p= into the filters and the controls.
      // Values that are not among the options are ignored. Returns true when
      // the address asks for anything other than the plain first page.
      readUrlState() {
        const params = new URLSearchParams(window.location.search);
        const q = (params.get('q') || '').trim();
        const tag = params.get('tag') || '';
        const product = params.get('product') || '';
        const page = parseInt(params.get('p'), 10);

        this.searchRaw = q;
        this.filters.search = q.toLowerCase();
        if (this.searchInput) this.searchInput.value = q;

        const hasOption = (select, value) =>
          !!select && Array.from(select.options).some((option) => option.value === value);
        this.filters.tag = tag && hasOption(this.tagSelect, tag) ? tag : '';
        if (this.tagSelect) this.tagSelect.value = this.filters.tag;
        this.filters.product = product && hasOption(this.productSelect, product) ? product : '';
        if (this.productSelect) this.productSelect.value = this.filters.product;

        this.currentPage = page > 1 ? page : 1;
        return !!(this.filters.search || this.filters.tag || this.filters.product || this.currentPage > 1);
      }

      // Puts the current state in the address. 'push' adds a Back step,
      // 'replace' just updates the address. Other parameters are kept.
      writeUrl(mode) {
        if (!window.history || !window.history.pushState) return;
        const url = new URL(window.location.href);
        const set = (key, value) => {
          if (value) url.searchParams.set(key, value);
          else url.searchParams.delete(key);
        };
        set('q', this.searchRaw);
        set('tag', this.filters.tag);
        set('product', this.filters.product);
        set('p', this.currentPage > 1 ? String(this.currentPage) : '');
        if (url.href === window.location.href) return;
        const state = { blogArchive: this.sectionId };
        if (mode === 'push') window.history.pushState(state, '', url.href);
        else window.history.replaceState(state, '', url.href);
      }

      async fetchRemainingPages() {
        const requests = [];
        for (let page = 2; page <= this.totalPages; page++) {
          const pageUrl = new URL(window.location.href);
          pageUrl.searchParams.set('section_id', this.sectionId);
          pageUrl.searchParams.set('page', page);
          requests.push(
            fetch(pageUrl.toString())
              .then((response) => response.text())
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const root = doc.querySelector(`[data-section-id="${this.sectionId}"]`);
                return root ? this.readEmbeddedArticles(root) : [];
              })
              .catch(() => [])
          );
        }

        const results = await Promise.all(requests);
        results.forEach((pageArticles) => {
          this.allArticles.push(...pageArticles);
        });
        this.populateTagFilter();
        this.populateProductFilter();
        this.applyFilters();
      }

      populateTagFilter() {
        if (!this.tagSelect) return;
        const currentValue = this.tagSelect.value;
        const tags = new Set();
        this.allArticles.forEach((article) => (article.tags || []).forEach((tag) => tags.add(tag)));

        this.tagSelect.innerHTML = `<option value="">${this.escapeHtml(this.tagFilterAllLabel)}</option>`;
        Array.from(tags)
          .sort((a, b) => a.localeCompare(b))
          .forEach((tag) => {
            const option = document.createElement('option');
            option.value = tag;
            option.textContent = tag;
            this.tagSelect.appendChild(option);
          });

        if (tags.has(currentValue)) this.tagSelect.value = currentValue;
      }

      populateProductFilter() {
        if (!this.productSelect) return;
        const currentValue = this.productSelect.value;
        const products = new Set();
        this.allArticles.forEach((article) => (article.products || []).forEach((product) => products.add(product)));

        this.productSelect.innerHTML = `<option value="">${this.escapeHtml(this.productFilterAllLabel)}</option>`;
        Array.from(products)
          .sort((a, b) => a.localeCompare(b))
          .forEach((product) => {
            const option = document.createElement('option');
            option.value = product;
            option.textContent =
              this.productFilterFormat === 'as_entered'
                ? product
                : product.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
            this.productSelect.appendChild(option);
          });

        if (products.has(currentValue)) this.productSelect.value = currentValue;
      }

      bindEvents() {
        if (this.searchInput) {
          let debounceTimer;
          this.searchInput.addEventListener('input', (event) => {
            clearTimeout(debounceTimer);
            const value = event.target.value;
            debounceTimer = setTimeout(() => {
              this.searchRaw = value.trim();
              this.filters.search = this.searchRaw.toLowerCase();
              this.currentPage = 1;
              this.applyFilters();
              this.writeUrl('replace');
            }, 200);
          });
        }

        if (this.tagSelect) {
          this.tagSelect.addEventListener('change', (event) => {
            this.filters.tag = event.target.value;
            this.currentPage = 1;
            this.applyFilters();
            this.writeUrl('push');
          });
        }

        if (this.productSelect) {
          this.productSelect.addEventListener('change', (event) => {
            this.filters.product = event.target.value;
            this.currentPage = 1;
            this.applyFilters();
            this.writeUrl('push');
          });
        }
      }

      applyFilters() {
        this.filtered = this.allArticles.filter((article) => {
          if (this.filters.search) {
            const haystack = `${article.title} ${article.excerpt}`.toLowerCase();
            if (!haystack.includes(this.filters.search)) return false;
          }
          if (this.filters.tag) {
            if (!(article.tags || []).includes(this.filters.tag)) return false;
          }
          if (this.filters.product) {
            if (!(article.products || []).includes(this.filters.product)) return false;
          }
          return true;
        });
        this.renderCurrentPage();
      }

      renderCurrentPage(options = {}) {
        const pages = Math.ceil(this.filtered.length / this.perPage) || 1;
        this.currentPage = Math.min(Math.max(1, this.currentPage), pages);
        const start = (this.currentPage - 1) * this.perPage;
        const pageArticles = this.filtered.slice(start, start + this.perPage);

        if (!options.skipGrid && this.gridEl) {
          // From here on the grid holds exactly one page of cards, so the
          // CSS that hides the server's extra cards on phones must let go.
          this.gridEl.removeAttribute('data-server-rendered');
          this.gridEl.innerHTML = '';
          pageArticles.forEach((article) => this.gridEl.appendChild(this.renderCard(article)));
        }

        if (this.countEl) {
          const count = this.filtered.length;
          this.countEl.textContent = `Showing ${count} ${count === 1 ? 'post' : 'posts'}`;
        }

        this.renderPagination();

        // Only touch empty-state / grid visibility once we've actually
        // redrawn the grid from real (search/filter/fetch) data — never
        // on the initial server-rendered paint.
        if (!options.skipGrid) {
          const isEmpty = this.filtered.length === 0;
          if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
          if (this.gridEl) this.gridEl.style.display = isEmpty ? 'none' : 'grid';
        }
      }

      // The twin of the star row in snippets/blog-archive-card.liquid, down to
      // the class names and the half-star rounding. The glyph CSS it relies on
      // is rendered once by the section (snippets/recipe-rating-css.liquid), so
      // nothing is injected here.
      renderStars(rating, count) {
        const value = Math.min(5, Math.max(0, parseFloat(rating) || 0));
        const halves = Math.round(value * 2);
        let stars = '';
        for (let i = 1; i <= 5; i++) {
          const fullAt = i * 2;
          const state = halves >= fullAt ? 'on' : halves === fullAt - 1 ? 'half' : 'off';
          stars += `<span class="lsr-star lsr-star--${state}"></span>`;
        }
        const display = Math.round(value * 10) / 10;
        return `<div class="blog-archive__card-rating"><span class="lsr-stars" role="img" aria-label="Average rating: ${display} out of 5" style="font-size: 14px">${stars}</span><span class="blog-archive__card-rating-count">(${count})</span></div>`;
      }

      renderCard(article) {
        const el = document.createElement('div');
        el.className = 'blog-archive__card';

        // Same image versions and sizes as snippets/blog-archive-card.liquid.
        const imageHtml = article.image
          ? `<div class="blog-archive__card-image-wrapper"><img src="${article.image}"${
              article.image_small
                ? ` srcset="${article.image_small} 400w, ${article.image} 800w" sizes="(min-width: 990px) 400px, 100vw"`
                : ''
            } alt="${this.escapeHtml(article.image_alt || article.title)}" loading="lazy"${
              article.image_w ? ` width="${article.image_w}" height="${article.image_h}"` : ''
            } class="blog-archive__card-image"></div>`
          : '';
        const titleHtml = this.showTitle
          ? `<h3 class="blog-archive__card-title">${this.escapeHtml(article.title)}</h3>`
          : '';

        const ratingCount = parseInt(article.rating_count, 10) || 0;
        const ratingHtml = this.showRatings && ratingCount > 0 ? this.renderStars(article.rating, ratingCount) : '';

        const metaParts = [];
        if (this.showAuthor && article.author) metaParts.push(this.escapeHtml(article.author));
        if (this.showDate && article.date_display) metaParts.push(this.escapeHtml(article.date_display));
        const metaHtml = metaParts.length
          ? `<div class="blog-archive__card-meta">${metaParts.map((part) => `<span>${part}</span>`).join('')}</div>`
          : '';

        const tagsHtml =
          this.showTags && article.tags && article.tags.length
            ? `<div class="blog-archive__card-tags">${article.tags
                .map(
                  (tag) =>
                    `<span class="blog-archive__card-tag color-${this.accentScheme}">${this.escapeHtml(tag)}</span>`
                )
                .join('')}</div>`
            : '';

        el.innerHTML = `
          <a href="${article.url}" class="blog-archive__card-link">
            ${imageHtml}
            <div class="blog-archive__card-content">
              ${titleHtml}
              ${ratingHtml}
              ${article.excerpt ? `<p class="blog-archive__card-excerpt">${this.escapeHtml(article.excerpt)}</p>` : ''}
              ${metaHtml}
              ${tagsHtml}
            </div>
          </a>
        `;
        return el;
      }

      renderPagination() {
        if (!this.paginationEl) return;
        const totalPages = Math.ceil(this.filtered.length / this.perPage) || 1;
        this.paginationEl.innerHTML = '';
        if (totalPages <= 1) return;

        const makeButton = (label, page, opts = {}) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'blog-archive__page-button' + (opts.active ? ' is-active' : '');
          btn.textContent = label;
          if (opts.disabled) btn.disabled = true;
          btn.addEventListener('click', () => {
            this.currentPage = page;
            this.renderCurrentPage();
            this.writeUrl('push');
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
          return btn;
        };

        this.paginationEl.appendChild(
          makeButton('Previous', this.currentPage - 1, { disabled: this.currentPage === 1 })
        );
        for (let i = 1; i <= totalPages; i++) {
          this.paginationEl.appendChild(makeButton(String(i), i, { active: i === this.currentPage }));
        }
        this.paginationEl.appendChild(
          makeButton('Next', this.currentPage + 1, { disabled: this.currentPage === totalPages })
        );
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text == null ? '' : String(text);
        return div.innerHTML;
      }
    }
  );
}